import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { PulseIndicator, usePulse, pulseLevelStyle } from "@/components/pulse-indicator";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { MapPin, Flag, MessageCircle, MessageSquareCheck, Network } from "lucide-react";

const LEVEL_LABEL: Record<"quiet" | "steady" | "rising" | "surge", string> = {
  quiet: "Quiet",
  steady: "Steady",
  rising: "Busier",
  surge: "Very busy",
};

export const Route = createFileRoute("/pulse")({
  component: PulsePage,
  head: () => ({
    meta: [
      { title: "Xamseen — Pulse" },
      { name: "description", content: "The live signal of your community — what is happening around you right now on Xamseen." },
      { property: "og:title", content: "Pulse — the live signal of your community" },
      { property: "og:description", content: "Feel what is happening around you: new issues, verifications, answers and projects as they land." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
});

type Signal = {
  id: string;
  kind: "issue" | "discussion" | "question" | "project";
  title: string;
  place: string | null;
  at: string;
  author: string | null;
};

const KIND: Record<Signal["kind"], { icon: typeof Flag; tone: string; label: string }> = {
  issue: { icon: Flag, tone: "text-ember", label: "Issue" },
  discussion: { icon: MessageCircle, tone: "text-tide", label: "Signal" },
  question: { icon: MessageSquareCheck, tone: "text-sun", label: "Question" },
  project: { icon: Network, tone: "text-moss", label: "Project" },
};

function PulsePage() {
  const { profile } = useAuth();
  const place = profile?.city ?? null;
  const pulse = usePulse(place);
  const tone = pulseLevelStyle(pulse.level);
  const [signals, setSignals] = useState<Signal[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await supabase
      .from("posts")
      .select("id, title, content, post_type, location_name, created_at, author:profiles!posts_author_profile_fkey(username, full_name)")
      .order("created_at", { ascending: false })
      .limit(30);
    setSignals(
      (data ?? []).map((p: any) => ({
        id: p.id,
        kind: p.post_type === "issue" || p.post_type === "emergency" ? "issue"
          : p.post_type === "question" ? "question"
          : p.post_type === "project" ? "project" : "discussion",
        title: p.title || String(p.content ?? "").slice(0, 90),
        place: p.location_name ?? null,
        at: p.created_at,
        author: p.author?.full_name ?? p.author?.username ?? null,
      }))
    );
    setLoading(false);
  };

  useEffect(() => {
    void load();
    const ch = supabase
      .channel("pulse-stream")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "posts" }, () => void load())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, []);

  return (
    <AppShell rightRail={<PulseRail pulse={pulse} />}>
      <div className="space-y-6">
        {/* Heartbeat hero */}
        <section className="panel panel-accent overflow-hidden px-5 py-7 text-center sm:px-8 sm:py-10">
          <div className="flex justify-center">
            <PulseIndicator level={pulse.level} intensity={pulse.intensity} size={44} />
          </div>
          <h1 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl">{pulse.headline}</h1>
          <p className={cn("mt-2 text-sm font-medium", tone.text)}>{pulse.detail}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm">
            <Stat value={pulse.lastHour} label="last hour" />
            <Stat value={pulse.lastDay} label="last 24 hours" />
            <Stat value={pulse.nearby} label={place ? `in ${place}` : "nearby"} />
          </div>
        </section>

        <div>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Live signal</h2>
          {loading ? (
            <div className="panel px-5 py-10 text-center text-sm text-muted-foreground">Loading activity…</div>
          ) : signals.length === 0 ? (
            <div className="panel px-5 py-12 text-center">
              <p className="text-sm font-semibold">The signal is quiet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Be the first heartbeat — <Link to="/report" className="font-medium text-foreground hover:underline">report something you saw</Link>.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {signals.map((s, i) => {
                const k = KIND[s.kind];
                const Icon = k.icon;
                return (
                  <li key={s.id} className="rise-in" style={{ animationDelay: `${Math.min(i, 10) * 45}ms` }}>
                    <Link
                      to="/posts/$id"
                      params={{ id: s.id }}
                      className="panel flex items-start gap-3 px-4 py-3.5 transition-colors hover:border-foreground/30"
                    >
                      <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-muted">
                        <Icon className={cn("h-4 w-4", k.tone)} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{s.title || "Untitled signal"}</p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                          <span className={k.tone}>{k.label}</span>
                          {s.place && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.place}</span>}
                          {s.author && <span>· {s.author}</span>}
                          <span>· {formatDistanceToNow(new Date(s.at), { addSuffix: true })}</span>
                        </p>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <span className="inline-flex flex-col">
      <b className="font-display text-xl font-bold tabular-nums">{value}</b>
      <span className="text-xs text-muted-foreground">{label}</span>
    </span>
  );
}

function PulseRail({ pulse }: { pulse: ReturnType<typeof usePulse> }) {
  return (
    <div className="space-y-4 py-3">
      <div className="panel px-4 py-4">
        <h3 className="font-display text-[15px] font-semibold">How Pulse works</h3>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Pulse listens to reports, verifications, answers and projects as they land. The heartbeat speeds up when your
          area gets busy and settles when things are calm.
        </p>
      </div>
      <div className="panel px-4 py-4">
        <h3 className="font-display text-[15px] font-semibold">What's driving it</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {pulse.located ? `Within ${pulse.radiusKm} km of you` : pulse.place ? `Scoped to ${pulse.place}` : "Platform-wide"}
        </p>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-border">
          <div
            className="h-full rounded-full bg-gradient-to-r from-moss via-sun to-ember transition-[width] duration-700"
            style={{ width: `${Math.max(3, Math.round(pulse.intensity * 100))}%` }}
          />
        </div>
        <ul className="mt-3 divide-y divide-border text-sm">
          {[
            { label: "Reports nearby (1h)", value: pulse.nearbyHour },
            { label: "New reports (1h)", value: pulse.postsHour },
            { label: "Replies (1h)", value: pulse.commentsHour },
            { label: "Verifications (1h)", value: pulse.verificationsHour },
            { label: "Active alerts", value: pulse.alertsActive },
            { label: "Resolved today", value: pulse.resolvedDay },
          ].map((r) => (
            <li key={r.label} className="flex items-center justify-between py-2">
              <span className="text-muted-foreground">{r.label}</span>
              <b className="font-semibold tabular-nums">{r.value}</b>
            </li>
          ))}
        </ul>
      </div>
      <div className="panel px-4 py-4">
        <h3 className="mb-3 font-display text-[15px] font-semibold">Activity levels</h3>
        <ul className="space-y-3 text-sm">
          {(["quiet", "steady", "rising", "surge"] as const).map((l) => (
            <li key={l} className="flex items-center gap-3">
              <PulseIndicator level={l} size={10} />
              <span className={cn(pulse.level === l ? "font-semibold" : "text-muted-foreground")}>{LEVEL_LABEL[l]}</span>
            </li>
          ))}
        </ul>
      </div>
      <Link to="/map" className="panel flex items-center gap-3 px-4 py-3.5 text-sm hover:border-foreground/30">
        <MapPin className="h-4 w-4 text-foreground" /> See it on the Map
      </Link>
    </div>
  );
}
